import React, { useState } from "react";
import AddUserModal from "../../components/modal/AddUserModal";
import DeleteConfirmationModal from "../../components/modal/DeleteConfirmationModal";
import DeviceMenu from "../device/DeviceMenu";
import { IUserModel } from "./CompanyPage";
import { useDeleteUser, useGenerateCredentials, useUpdateUserRole } from "../../queries/admin";
import { queryClient } from "../../queries/client";
import SendNotification from "../../components/modal/SendNotification";
import { toast } from "react-toastify";

const CompanyUser = ({ users }: { users: IUserModel[] }) => {
  const [dialog, setDialog] = useState(false);
  const [sendNotification, setSendNotification] = useState(false);
  const [email, setEmail] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<IUserModel | null>(null);
  const { mutate: generateCredentials } = useGenerateCredentials();
  const { mutate: deleteUser } = useDeleteUser();
  const { mutate: updateUserRole } = useUpdateUserRole();

  const confirmDeleteUser = () => {
    if (userToDelete) {
      deleteUser(userToDelete.id, {
        onSuccess() {
          queryClient.invalidateQueries({
            queryKey: ["clients"],
          });
          toast.success("User deleted successfully");
          setShowDeleteModal(false);
          setUserToDelete(null);
        },
        onError(error: any) {
          toast.error(
            error?.response?.data?.errorMessage || "Failed to delete user",
          );
          setShowDeleteModal(false);
          setUserToDelete(null);
        },
      });
    }
  };

  return (
    <div className="px-4 sm:px-6 col-span-2 lg:px-8 dark:bg-[#111B21] pt-6 border border-borderColor shadow-md rounded-md">
      {dialog && (
        <AddUserModal
          isOpen={dialog}
          onClose={() => {
            setDialog(false);
          }}
        />
      )}
      {sendNotification && (
        <SendNotification
          isOpen={sendNotification}
          onClose={() => {
            setSendNotification(false);
            setEmail("");
          }}
          email={email}
        />
      )}

      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
            Company User
          </h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 leading-0">
            {users.length} users
          </p>
        </div>
        {
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              onClick={() => {
                setDialog(true);
              }}
              className="block cursor-pointer rounded-md bg-primary px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              Add User
            </button>
          </div>
        }
      </div>
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 h-full overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block h-full min-w-full py-2 align-middle">
            <table className="min-w-full h-full divide-y divide-gray-300 dark:divide-gray-700">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 dark:text-white sm:pl-6 lg:pl-8"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white"
                  >
                    Mail
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white"
                  >
                    Role
                  </th>

                  <th
                    scope="col"
                    className="relative py-3.5 pl-3 pr-4 sm:pr-6 lg:pr-8"
                  >
                    <span className="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y h-full divide-gray-200 dark:divide-gray-700 dark:bg-[#111B21]">
                {users.map((user) => (
                  <tr key={user.email}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 dark:text-white sm:pl-6 lg:pl-8">
                      {user.name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-300">
                      {user.email}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-300">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          user.role === "ADMIN"
                            ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                            : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>

                    <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 lg:pr-8">
                      <DeviceMenu
                        menu={[
                          {
                            name: "Generate Credential",
                            action: () => {
                              generateCredentials(user.email, {
                                onSuccess() {
                                  toast(
                                    "Password Generated and Sent over mail",
                                    {
                                      type: "success",
                                      autoClose: 2000,
                                    },
                                  );
                                },
                                onError(data: any) {
                                  toast(data.response.data.errorMessage, {
                                    type: "error",
                                    autoClose: 2000,
                                  });
                                },
                              });
                            },
                          },
                          {
                            name: user.role === "ADMIN" ? "Make User" : "Make Admin",
                            action: () => {
                              const newRole = user.role === "ADMIN" ? "USER" : "ADMIN";
                              updateUserRole(
                                { id: user.id, role: newRole },
                                {
                                  onSuccess() {
                                    queryClient.invalidateQueries({
                                      queryKey: ["clients"],
                                    });
                                  },
                                },
                              );
                            },
                          },
                          {
                            name: "Send Notification",
                            action: () => {
                              setSendNotification(true);
                              setEmail(user.email);
                              // SendNotification
                            },
                          },
                          {
                            name: "Delete",
                            action: () => {
                              setUserToDelete(user);
                              setShowDeleteModal(true);
                            },
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}
          onConfirm={confirmDeleteUser}
          title="Delete User"
          itemName={userToDelete.name}
          itemType="user"
          customMessage={`Are you sure you want to delete user "${userToDelete.name}" (${userToDelete.email})? This action cannot be undone and the user will lose access to all associated devices and data.`}
        />
      )}
    </div>
  );
};

export default CompanyUser;
